import $ from 'jquery';
import imagesLoaded from 'imagesloaded';
import NavTrigger from '../snippets/NavTrigger';
import MeganavMenu from '../snippets/MeganavMenu';
import FadePanel from '../snippets/FadePanel';
import layout from '../Layout.js';

export default class MeganavStaticSection {
  constructor(section) {
    this.$el = $(section.el);
    this.$window = $(window);

    // Editor check
    this.isThemeEditor = window.Shopify && window.Shopify.designMode;

    this.trigger = section.data.trigger;
    this.postMessage = section.postMessage;
    this.clones = [];

    if (this.isThemeEditor || this.trigger) {
      this.clone();
    }

    this.navTrigger = new NavTrigger({
      el: section.el,
      namespace: 'meganav',
      trigger: this.trigger,
      onShow: () => {
        this.update();
        this.postMessage('nav:close-all', {});
        this.postMessage('nav:close-triggers', { source: this.$el });
        this.postMessage('nav:show-overlay', { source: this.$el });
      },
    });

    // Ensure images have the same height
    this.$window.on('resize.meganav', event => this.update());

    // Fade out edges when scrolling horizontally
    this.fadePanel = new FadePanel('[data-meganav-clone-source] .meganav-menu-panel-contents');

    // Handle breakpoint changes while editing
    this.layoutHandler = this.onBreakpointChange.bind(this);
    layout.onBreakpointChange(this.layoutHandler);

    // Re-layout items as images load
    this.onImageLoad = this.update.bind(this);
    this.imagesLoaded = new imagesLoaded(this.$el[0]);
    this.imagesLoaded.on('progress', this.onImageLoad);
  }

  onSectionUnload() {
    this.clones.forEach(clone => {
      clone.$el.empty();
      clone.menu.unload();
    });

    this.$window.off('.meganav');

    this.navTrigger.unload();
    layout.offBreakpointChange(this.layoutHandler);

    this.imagesLoaded.off('progress', this.onImageLoad);
  }

  onSectionSelect() {
    this.isEditing = true;

    if (layout.isBreakpoint('M') || layout.isBreakpoint('L')) {
      this.navTrigger.lock();
    } else {
      this.postMessage('mobilenav:open');
    }
  }

  onSectionDeselect() {
    this.isEditing = false;

    if (layout.isBreakpoint('M') || layout.isBreakpoint('L')) {
      this.navTrigger.unlock();
    } else {
      this.postMessage('mobilenav:close');
    }
  }

  onSectionBlockSelect(block) {
    const $el = $(block.el);
    const $container = $el.parent();

    $container.scrollLeft(
      $el.offset().left - ($el.width() / 2) - ($container.width() / 2)
    );
  }

  onSectionMessage(name, data) {
    if (name === 'nav:close-triggers' && data.source !== this.$el) {
      this.navTrigger.hide();
    }
    if (name === 'nav:show-overlay') {
      this.navTrigger.showOverlay();
    }
  }

  onBreakpointChange() {
    if (!this.isEditing) return;

    if (layout.isBreakpoint('M') || layout.isBreakpoint('L')) {
      this.navTrigger.lock();
      this.postMessage('mobilenav:close');
    } else {
      this.navTrigger.unlock();
      this.postMessage('mobilenav:open');
    }
  }

  /**
   * Copy the mega nav to other parts of the theme.
   */
  clone() {
    const $source = this.$el.find('[data-meganav-clone-source]').first();
    if (!$source.length) return;

    const $clone = $source.clone().removeAttr('data-meganav-clone-source');

    // Add meganav to mobilenav panel
    const $mobileTarget = $('[data-meganav-mobile-target]');

    if ($mobileTarget.length) {
      // Remove Theme Editor block data attributes so the Theme Editor
      // doesn't get confused about multiple elements having the
      // `block.shopify_attributes` attributes.
      $clone.find('.meganav-menu-group').each((index, el) => this._removeDataAttributes(el));

      $mobileTarget.append($clone);

      this.clones.push({
        $el: $mobileTarget,
        menu: new MeganavMenu($mobileTarget),
      });
    }
  }

  update() {
    this.fadePanel.update();
    this._updateImageHeights();
  }

  /**
   * Set meganav collection image heights to match the tallest.
   *
   * This is purely visual, to make all the collection titles line up nicely.
   */
  _updateImageHeights() {
    const $images = this.$el.find('.meganav-menu-image');
    if (!$images.length) return;

    // Find maximum image height
    let height = 0;
    $images.each((index, el) => {
      const $img = $(el).find('img, svg').first();
      if (!$img.length) return;

      height = Math.max(height, $img[0].clientHeight);
    });

    // Set all images height to maximum
    if (height) {
      $images.each((index, el) => $(el).height(height));
    }
  }

  /**
   * Remove all data attributes from an element.
   *
   * @param {Element} el
   */
  _removeDataAttributes(el) {
    if (!el || !el.attributes) return;

    const attributesToRemove = [];

    for (let i = el.attributes.length - 1; i >= 0; i--) {
      const name = el.attributes[i].name;

      if (name.substring(0, 5) === 'data-') {
        attributesToRemove.push(name);
      }
    }

    attributesToRemove.forEach(name => el.removeAttribute(name));
  }
}
